import { useUserOrgId } from '../../../auth/authentication';
import { AxleInfoField } from '../../../components/displayFields/AxleInfoField';
import { QualificationsField } from '../../../components/formFields/QualificationsField';
import { TerritoriesField } from '../../../components/formFields/TerritoriesField';
import { AxleCheckboxField } from '../../../components/formFields/checkboxes';
import {
    AxleDialog,
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogPrimaryButton,
} from '../../../components/layout/AxleDialog';
import { Outline } from '../../../components/layout/Outline';
import { If } from '../../../components/utils/conditionalComponents';
import { VerticalStack } from '../../../components/utils/linearLayouts';
import {
    SupplyConnection,
    useEditSupplyConnection,
    useEditSupplyConnectionOptions,
} from '../../../fetching/api/supplyAndDemandConnections';
import { useInputState, useJointInputState } from '../../../utils/inputState';
import { ReturnComponent } from '../../../utils/reactTypes';

const EditSupplyConnectionDialogContents = ({
    close,
    supplyConnection,
}: {
    close: () => void;
    supplyConnection: SupplyConnection;
}): ReturnComponent => {
    const userOrgId = useUserOrgId();
    const { orgLevelQualificationOptions, passthroughQualificationOptions, territoryOptions } =
        useEditSupplyConnectionOptions(supplyConnection.handshake.uuid);

    const editSupplyConnection = useEditSupplyConnection(supplyConnection.handshake.uuid);

    const orgLevelQualificationIdsState = useInputState<string[]>(
        supplyConnection.org_qualifications.map((q) => q.uuid),
        {
            defaultHelperText: `These qualifications will be applied to ${supplyConnection.handshake.to_org.name} as a whole.`,
        },
    );
    const passthroughQualificationIdsState = useInputState<string[]>(
        supplyConnection.passthrough_qualifications.map((q) => q.uuid),
        {
            defaultHelperText: `These qualifications will be be available to ${supplyConnection.handshake.to_org.name}, to individually apply to specific clinicians in their organization.`,
        },
    );

    const hasTerritoryRestrictionState = useInputState<boolean>(
        supplyConnection.handshake.has_territory_restriction,
    );
    const territoriesState = useInputState<string[]>(
        supplyConnection.handshake.territories.map((territory) => territory.uuid),
        { required: true },
    );

    const { checkDifferent, checkValid } = useJointInputState([
        orgLevelQualificationIdsState,
        passthroughQualificationIdsState,
        hasTerritoryRestrictionState,
        ...(hasTerritoryRestrictionState.value ? [territoriesState] : []),
    ]);

    return (
        <>
            <AxleDialogContent>
                <If condition={supplyConnection.handshake.from_org.uuid !== userOrgId}>
                    <AxleInfoField
                        label={'Organization'}
                        value={`${supplyConnection.handshake.from_org.name}`}
                    />
                </If>
                <AxleInfoField
                    label={'Supply Connection'}
                    value={`${supplyConnection.handshake.to_org.name}`}
                />
                <VerticalStack spacing={2}>
                    <Outline>
                        <QualificationsField
                            label={'Org-level Qualifications'}
                            inputState={orgLevelQualificationIdsState}
                            options={orgLevelQualificationOptions}
                        />
                        <QualificationsField
                            label={'Individual-level Qualifications'}
                            inputState={passthroughQualificationIdsState}
                            options={passthroughQualificationOptions}
                        />
                    </Outline>
                    <Outline>
                        <AxleCheckboxField
                            checkboxText={'Restrict this handshake geographically'}
                            inputState={hasTerritoryRestrictionState}
                        />
                        <If condition={hasTerritoryRestrictionState.value}>
                            <TerritoriesField
                                inputState={territoriesState}
                                options={territoryOptions}
                            />
                        </If>
                    </Outline>
                </VerticalStack>
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogPrimaryButton
                    label={'Save'}
                    onClick={() => {
                        if (!checkDifferent()) return;
                        if (!checkValid()) return;
                        void editSupplyConnection(
                            {
                                org_level_qualification_uuids:
                                    orgLevelQualificationIdsState.valueIfDifferentElseUndefined,
                                passthrough_qualification_uuids:
                                    passthroughQualificationIdsState.valueIfDifferentElseUndefined,
                                has_territory_restriction:
                                    hasTerritoryRestrictionState.valueIfDifferentElseUndefined,
                                territory_uuids: territoriesState.valueIfDifferentElseUndefined,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

export const EditSupplyConnectionDialog = ({
    isOpen,
    close,
    supplyConnection,
}: {
    isOpen: boolean;
    close: () => void;
    supplyConnection: SupplyConnection | null;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='edit-supply-connection-dialog'
            isOpen={isOpen}
            close={close}
            title={'Edit Supply Connection'}
        >
            {supplyConnection && (
                <EditSupplyConnectionDialogContents
                    close={close}
                    supplyConnection={supplyConnection}
                />
            )}
        </AxleDialog>
    );
};
